import React, { useCallback, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { TopNav } from '@decisiv/ui-components';

// KDS TopNav.Button drops the Popover's aria attributes and remounts the
// <button> when it has a tooltip and opens, so the state is re-applied through
// a callback ref; tokens.css restyles the open trigger at night via aria-expanded.
export default function NavMenu({
  triggerRef = null,
  contentRole = 'dialog',
  onShow = noop,
  onHide = noop,
  ...rest
}) {
  const trigger = useRef(null);
  const open = useRef(false);
  useImperativeHandle(triggerRef, () => trigger.current);

  const apply = (node) => {
    if (!node) return;
    node.setAttribute('aria-haspopup', contentRole);
    node.setAttribute('aria-expanded', String(open.current));
  };

  const attach = useCallback((node) => {
    trigger.current = node;
    apply(node);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply reads refs and a stable prop
  }, []);

  const mark = (value) => {
    open.current = value;
    apply(trigger.current);
  };

  return (
    <TopNav.Menu
      ref={attach}
      contentRole={contentRole}
      onShow={(event) => {
        mark(true);
        onShow(event);
      }}
      onHide={(event) => {
        mark(false);
        onHide(event);
      }}
      {...rest}
    />
  );
}

NavMenu.propTypes = {
  triggerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  contentRole: PropTypes.string,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
};
